"use client"

import * as React from "react"
import Link from "next/link"
import { ArrowRightIcon } from "@/components/brand-kit/icon-sprite"
import { SectionHeader } from "./section-header"
import { stripBrandPrefix, type BrandCollection } from "./types"
import { Badge, makeBadge } from "@/components/badges"

interface CollectionsListProps {
  brandName: string
  collections: BrandCollection[]
}

/** Priority buckets for sorting the brand's collections (handoff §4). Items
 *  inside a bucket keep their input order so display_order from the DB is
 *  preserved within each tier. Lower index = surfaced earlier. */
const PRIORITY_TIERS: { label: string; matchers: RegExp[] }[] = [
  // 1. Style / type
  { label: "type", matchers: [
    /french[- ]door/i, /side[- ]by[- ]side/i, /top[- ]freezer/i, /bottom[- ]freezer/i,
    /counter[- ]depth/i, /4[- ]door/i, /built[- ]in/i, /compact/i, /mini[- ]fridge/i,
    /beverage cooler/i, /wine cooler/i,
  ]},
  // 2. Smart / features
  { label: "features", matchers: [/smart/i, /ice maker/i, /water dispenser/i, /instaview/i] },
  // 3. Finish
  { label: "finish", matchers: [/stainless steel/i, /black stainless/i, /white/i, /black/i] },
  // 4. Size
  { label: "size", matchers: [/\d+\s*inch/i, /\d+\s*"\s*wide/i] },
  // 5. Price
  { label: "price", matchers: [/under \$/i] },
]

interface PreparedRow {
  baseLabel: string
  slug: string
  hasEnergyStar: boolean
  bucket: number
}

/** Dedupe `<Brand> Foo` and `<Brand> Foo Energy Star` into a single row with
 *  an inline Energy Star chip when both variants exist (handoff §4), then
 *  interleave the rows across priority buckets so the first 12 visible
 *  cover a mix of type / features / finishes / size / price instead of
 *  flooding the user with one category (the previous "type-first" sort
 *  meant a brand with 8 type collections + 2 sizes + 2 finishes would never
 *  surface anything but type in the default 12). */
function prepareRows(brandName: string, collections: BrandCollection[]): PreparedRow[] {
  const byBase = new Map<string, PreparedRow>()
  for (const c of collections) {
    const stripped = stripBrandPrefix(c.title, brandName)
    const energyStar = / energy star$/i.test(stripped)
    const base = stripped.replace(/ energy star$/i, "").trim()
    const existing = byBase.get(base.toLowerCase())
    if (existing) {
      existing.hasEnergyStar = existing.hasEnergyStar || energyStar
      // Prefer the non-Energy-Star slug so the canonical collection page is
      // the landing target — the chip just signals that an Energy-Star cut
      // also exists.
      if (!energyStar) existing.slug = c.slug
    } else {
      byBase.set(base.toLowerCase(), {
        baseLabel: base,
        slug: c.slug,
        hasEnergyStar: energyStar,
        bucket: priorityFor(base),
      })
    }
  }
  // Group by bucket, sort within each bucket alphabetically, then round-
  // robin across buckets so the top of the list mixes categories.
  const buckets: PreparedRow[][] = PRIORITY_TIERS.map(() => [])
  const overflow: PreparedRow[] = []
  for (const r of byBase.values()) {
    if (r.bucket < PRIORITY_TIERS.length) buckets[r.bucket].push(r)
    else overflow.push(r)
  }
  for (const b of buckets) b.sort((a, b) => a.baseLabel.localeCompare(b.baseLabel))
  overflow.sort((a, b) => a.baseLabel.localeCompare(b.baseLabel))

  const interleaved: PreparedRow[] = []
  const cursors = buckets.map(() => 0)
  let remaining = byBase.size - overflow.length
  while (remaining > 0) {
    for (let i = 0; i < buckets.length; i++) {
      if (cursors[i] < buckets[i].length) {
        interleaved.push(buckets[i][cursors[i]++])
        remaining--
      }
    }
  }
  return interleaved.concat(overflow)
}

function priorityFor(label: string): number {
  for (let i = 0; i < PRIORITY_TIERS.length; i++) {
    if (PRIORITY_TIERS[i].matchers.some(re => re.test(label))) return i
  }
  return PRIORITY_TIERS.length // unknown → after all known tiers
}

const VISIBLE_DEFAULT = 12

/**
 * Compact 3-col text-link list of the brand's collections.
 *
 * Each row links to `/collections/{slug}`. The brand-name prefix is stripped
 * from each label and matching `Energy Star` variants are folded into the
 * base row with an inline amber chip. Show 12 by default; toggle expands /
 * collapses the rest.
 */
export function CollectionsList({ brandName, collections }: CollectionsListProps) {
  const rows = React.useMemo(() => prepareRows(brandName, collections), [brandName, collections])
  const [expanded, setExpanded] = React.useState(false)
  if (rows.length === 0) return null
  const visible = expanded ? rows : rows.slice(0, VISIBLE_DEFAULT)
  const hasOverflow = rows.length > VISIBLE_DEFAULT

  return (
    <section className="my-12">
      <SectionHeader
        eyebrow="Browse by"
        title={`${brandName} Collections`}
        action={hasOverflow ? {
          type: "button",
          label: expanded ? "Show less" : `See all ${rows.length}`,
          onClick: () => setExpanded(v => !v),
        } : undefined}
        className="mb-4"
      />
      <ul className="grid grid-cols-1 gap-x-6 border-t border-gray-100 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map(r => (
          <li key={r.slug} className="border-b border-gray-100">
            <Link
              href={`/collections/${r.slug}`}
              prefetch={false}
              className="group flex items-center gap-2.5 px-1 py-3 text-[14px] font-semibold text-gray-800 transition-all duration-150 hover:pl-2 hover:text-[#ff9951]"
            >
              <span className="min-w-0 flex-1 truncate">{r.baseLabel}</span>
              {/* FRIDGE-278: the canonical Energy Star badge. This rendered a
                  yellow uppercase chip with no ⚡ while the same badge is
                  green-700 with the emoji on every product surface — the
                  sidebar variant the owner's directive names. */}
              {r.hasEnergyStar && (
                <Badge badge={makeBadge('energy_star')} size="sm" />
              )}
              <ArrowRightIcon className="h-3 w-3 flex-shrink-0 text-gray-300 transition-colors group-hover:text-[#ff9951]" aria-hidden />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}
